import { IntlShape } from 'react-intl'
import {
  SubscriptionCycle,
  SubscriptionCycleOperators,
  SubscriptionFrequency,
  type Subscription,
} from 'typings'
import { create, enforce, omitWhen, test } from 'vest'
import { messages } from '../messages'

function isRange(cycle: SubscriptionCycle | undefined) {
  return !!cycle && cycle.operator === SubscriptionCycleOperators.RANGE
}

export function frequencyValidation(intl: IntlShape, name: string) {
  return create((frequency: SubscriptionFrequency | undefined) => {
    const { $t } = intl
    const { validation } = messages

    test(`${name}.value`, $t(validation.required), () => {
      enforce(frequency?.value).isNotUndefined()
    })

    test(`${name}.value`, $t(validation.greaterThan, { value: 0 }), () => {
      enforce(frequency?.value).greaterThan(0)
    })
  })
}

export function cycleValidation(intl: IntlShape, name: string) {
  return create((cycle: SubscriptionCycle | undefined) => {
    const { $t } = intl
    const { validation } = messages

    omitWhen(cycle?.operator === SubscriptionCycleOperators.RANGE, () => {
      const value = isRange(cycle) ? undefined : cycle?.value

      test(`${name}.value`, $t(validation.required), () => {
        enforce(value).isNotUndefined()
      })

      omitWhen(cycle?.operator !== SubscriptionCycleOperators.LESS_THAN, () => {
        test(`${name}.value`, $t(validation.greaterThan, { value: 1 }), () => {
          enforce(value).greaterThan(1)
        })
      })

      omitWhen(cycle?.operator === SubscriptionCycleOperators.LESS_THAN, () => {
        test(`${name}.value`, $t(validation.greaterThan, { value: 0 }), () => {
          enforce(value).greaterThan(0)
        })
      })
    })

    omitWhen(cycle?.operator !== SubscriptionCycleOperators.RANGE, () => {
      const { min, max } = isRange(cycle) ? cycle : {}

      test(`${name}.min`, $t(validation.required), () => {
        enforce(min).isNotUndefined()
      })

      test(`${name}.max`, $t(validation.required), () => {
        enforce(max).isNotUndefined()
      })

      test(`${name}.min`, $t(validation.greaterThan, { value: 0 }), () => {
        enforce(min).greaterThan(0)
      })

      test(`${name}.max`, $t(validation.greaterThanMin), () => {
        enforce(max).greaterThan(min)
      })
    })
  })
}

export function validationSuite(intl: IntlShape) {
  return create(({ filter }: Subscription) => {
    omitWhen(!filter.frequency, () => {
      frequencyValidation(intl, 'filter.frequency')(filter.frequency)
    })

    omitWhen(!filter.cycle, () => {
      cycleValidation(intl, 'filter.cycle')(filter.cycle)
    })
  })
}
